import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  FunnelIcon,
  UsersIcon
} from '@heroicons/react/24/outline';
import { Influencer, SearchFilters } from '../../types';
import { graphqlApi } from '../../services/graphql';
import { Spinner } from '../common/Spinner';
import { InfluencerCard } from './InfluencerCard';
import { SearchFiltersPanel } from './SearchFiltersPanel';

export const InfluencerSearch: React.FC = () => {
  const { t } = useTranslation('search');
  const [influencers, setInfluencers] = useState<Influencer[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [filters, setFilters] = useState<SearchFilters>({
    category: '',
    location: '',
    minFollowers: undefined,
    minRating: undefined,
    limit: 12
  });

  useEffect(() => {
    searchInfluencers();
  }, [filters]);

  const searchInfluencers = async () => {
    try {
      setIsLoading(true);
      const response = await graphqlApi.getInfluencers(filters);

      if (response.success && response.data) {
        setInfluencers(response.data.influencers || []);
        setTotalCount(response.data.total || 0);
      }
    } catch (error) {
      console.error('Error searching influencers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleFilterChange = (newFilters: SearchFilters) => {
    setFilters(newFilters);
  };

  const handleResetFilters = () => {
    setFilters({
      category: '',
      location: '',
      minFollowers: undefined,
      minRating: undefined,
      limit: 12
    });
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">{t('title')}</h1>
        <p className="text-gray-600">
          {t('subtitle')}
        </p>
      </div>

      {/* Search Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-4">
              <div className="relative flex-1 min-w-0">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  value={filters.category || ''}
                  onChange={(e) => handleFilterChange({ ...filters, category: e.target.value })}
                />
              </div>
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              >
                <AdjustmentsHorizontalIcon className="h-4 w-4 mr-2" />
                {t('filters')}
              </button>
            </div>
          </div>

          {/* Results Summary */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 text-sm text-gray-600">
              <UsersIcon className="h-4 w-4" />
              <span>
                {isLoading ? t('searching') : t('influencersFound', { count: totalCount })}
              </span>
            </div>

            {(filters.category || filters.location || filters.minFollowers || filters.minRating) && (
              <button
                onClick={handleResetFilters}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                {t('resetFilters')}
              </button>
            )}
          </div>
        </div>

        {/* Filters Panel */}
        {showFilters && (
          <div className="border-t border-gray-200">
            <SearchFiltersPanel
              filters={filters}
              onFiltersChange={handleFilterChange}
              onClose={() => setShowFilters(false)}
            />
          </div>
        )}
      </div>

      {/* Active Filters */}
      {(filters.category || filters.location || filters.minFollowers || filters.minRating) && (
        <div className="mb-6">
          <div className="flex items-center space-x-2 mb-2">
            <FunnelIcon className="h-4 w-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">{t('activeFilters')}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {filters.category && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-blue-100 text-blue-800">
                {t('category')}: {filters.category}
                <button
                  onClick={() => handleFilterChange({ ...filters, category: '' })}
                  className="ml-2 text-blue-600 hover:text-blue-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.location && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                {t('location')}: {filters.location}
                <button
                  onClick={() => handleFilterChange({ ...filters, location: '' })}
                  className="ml-2 text-green-600 hover:text-green-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.minFollowers && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-purple-100 text-purple-800">
                {t('minFollowers')}: {filters.minFollowers.toLocaleString()}
                <button
                  onClick={() => handleFilterChange({ ...filters, minFollowers: undefined })}
                  className="ml-2 text-purple-600 hover:text-purple-800"
                >
                  ×
                </button>
              </span>
            )}
            {filters.minRating && (
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                {t('minRating')}: {filters.minRating}+
                <button
                  onClick={() => handleFilterChange({ ...filters, minRating: undefined })}
                  className="ml-2 text-yellow-600 hover:text-yellow-800"
                >
                  ×
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Results */}
      {isLoading ? (
        <div className="flex justify-center items-center h-64">
          <Spinner size="large" />
        </div>
      ) : influencers.length === 0 ? (
        <div className="text-center py-12">
          <UsersIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('noInfluencersFound')}</h3>
          <p className="text-gray-500 mb-4">
            {t('adjustFilters')}
          </p>
          <button
            onClick={handleResetFilters}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
          >
            {t('resetFilters')}
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {influencers.map((influencer) => (
            <InfluencerCard key={influencer.influencerId} influencer={influencer} />
          ))}
        </div>
      )}

      {/* Load More */}
      {influencers.length > 0 && influencers.length < totalCount && (
        <div className="text-center mt-8">
          <button
            onClick={() => handleFilterChange({ ...filters, limit: (filters.limit || 12) + 12 })}
            className="inline-flex items-center px-6 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            {t('loadMore')}
          </button>
        </div>
      )}
    </div>
  );
};
